import _ from 'lodash';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { trackErrorType } from '@/track/track.utilities';
import { logWarn } from '@/utilities/logger';
import { SeeqNames } from '@seeqdev/constants';

export type UserProfileTrackAction = 'Edit Profile' | 'Edit System Preferences' | 'Edit Datasource Preferences';

export type UserProfileProperty = 'firstName' | 'lastName' | 'email' | 'role' | 'site' | 'manager' | 'password';

export type SystemPreferenceProperty =
  | 'timezone'
  | 'use24HourFormat'
  | 'dateFormat'
  | 'language'
  | 'preferNewTab'
  | 'darkMode';

export type DatasourcePreferenceProperty = 'datasourceSelection' | 'includeNewDatasources';

type SelectValue = string | { value?: string } | null | undefined;

type ProfileIdentity = {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  role?: string | null;
  site?: string | null;
  manager?: { id?: string } | null;
  isPasswordSettable: boolean;
};

type SubmittedProfile = {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: SelectValue;
  site?: string | null;
  manager?: { id?: string } | null;
};

/** Sends one User Profiles event for a save. Skips the event when nothing changed. Values are never included. */
export function trackUserProfileSave(
  action: UserProfileTrackAction,
  changedProperties: readonly string[],
  result: 'success' | 'failure',
  error?: unknown,
): void {
  if (changedProperties.length === 0) {
    return;
  }

  try {
    doTrack(TRACK.USER_PROFILES, action, {
      targetType: 'user',
      result,
      changedProperties: [...changedProperties],
      ...(result === 'failure' ? { errorType: trackErrorType(error) } : {}),
    });
  } catch (trackError) {
    logWarn(trackError instanceof Error ? trackError : new Error(String(trackError)), 'track');
  }
}

/** Field names whose submitted profile values differ from the signed-in user. */
export function changedProfileProperties(current: ProfileIdentity, submitted: SubmittedProfile): UserProfileProperty[] {
  const changed: UserProfileProperty[] = [];
  if (text(submitted.firstName) !== text(current.firstName)) {
    changed.push('firstName');
  }
  if (text(submitted.lastName) !== text(current.lastName)) {
    changed.push('lastName');
  }
  if (text(submitted.email) !== currentEmail(current)) {
    changed.push('email');
  }
  if (selectValue(submitted.role) !== text(current.role)) {
    changed.push('role');
  }
  if (text(submitted.site) !== text(current.site)) {
    changed.push('site');
  }
  if (managerId(submitted.manager) !== managerId(current.manager)) {
    changed.push('manager');
  }
  return changed;
}

/** Datasource preference names whose submitted values differ from the saved selection. */
export function changedDatasourcePreferenceProperties(
  current: { datasourceIds: readonly string[]; includeNewDatasources: boolean },
  submitted: { datasourceIds: readonly string[]; includeNewDatasources: boolean },
): DatasourcePreferenceProperty[] {
  const changed: DatasourcePreferenceProperty[] = [];
  if (!sameIds(current.datasourceIds, submitted.datasourceIds)) {
    changed.push('datasourceSelection');
  }
  if (current.includeNewDatasources !== submitted.includeNewDatasources) {
    changed.push('includeNewDatasources');
  }
  return changed;
}

function currentEmail(current: ProfileIdentity): string {
  if (current.isPasswordSettable) {
    return text(current.email) || text(current.username);
  }
  return text(current.email);
}

function managerId(manager: { id?: string } | null | undefined): string {
  if (_.isEmpty(manager) || !manager?.id || manager.id === SeeqNames.Database.Utils.EmptyGuid) {
    return SeeqNames.Database.Utils.EmptyGuid;
  }
  return manager.id;
}

function selectValue(value: SelectValue): string {
  if (typeof value === 'string') {
    return value;
  }
  return value?.value ?? '';
}

function text(value: string | null | undefined): string {
  return value ?? '';
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return sortedLeft.every((id, index) => id === sortedRight[index]);
}
